import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarClock, CheckCircle2, Circle, PlayCircle, Filter, ChevronDown, ChevronRight, ArrowRight } from 'lucide-react';
import api from '../lib/api';
import { CATEGORY_META, PRIORITY, STATUS } from '../lib/constants';

const STATUS_FLOW = { pendiente: 'en_curso', en_curso: 'completada', completada: 'pendiente' };
const STATUS_NEXT_LABEL = { pendiente: 'Iniciar', en_curso: 'Marcar completa', completada: 'Reabrir' };

export default function ActionPlan() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('todas');
  const [filterPriority, setFilterPriority] = useState('todas');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/action-plan');
      setTasks(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return tasks.filter((t) => {
      if (filterStatus !== 'todas' && t.status !== filterStatus) return false;
      if (filterPriority !== 'todas' && t.priority !== filterPriority) return false;
      return true;
    });
  }, [tasks, filterStatus, filterPriority]);

  const counts = useMemo(() => ({
    total: tasks.length,
    pendiente: tasks.filter((t) => t.status === 'pendiente').length,
    en_curso: tasks.filter((t) => t.status === 'en_curso').length,
    completada: tasks.filter((t) => t.status === 'completada').length,
  }), [tasks]);

  const advance = async (t) => {
    const next = STATUS_FLOW[t.status] || 'pendiente';
    try {
      const { data } = await api.patch(`/action-plan/tasks/${t.id}`, { status: next });
      setTasks((prev) => prev.map((x) => (x.id === t.id ? data : x)));
      toast.success(`Tarea → ${STATUS[next].label}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No pudimos actualizar la tarea.');
    }
  };

  if (loading) return <div className="text-sm text-[#6B7280]">Cargando plan…</div>;

  if (tasks.length === 0) {
    return (
      <div className="pv-card p-10 text-center" data-testid="plan-empty">
        <CheckCircle2 size={28} className="mx-auto text-[#2A9B68]" strokeWidth={1.5} />
        <div className="mt-3 text-lg font-semibold text-[#1C2329]">Aún no hay tareas generadas</div>
        <div className="text-sm text-[#6B7280] mt-1">Complete el diagnóstico inicial para obtener un plan priorizado.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total" value={counts.total} />
        <Stat label="Pendientes" value={counts.pendiente} color="#6B7280" />
        <Stat label="En curso" value={counts.en_curso} color="#2563EB" />
        <Stat label="Completadas" value={counts.completada} color="#2A9B68" />
      </div>

      {/* Filters */}
      <div className="pv-card p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid="plan-filters">
        <div className="flex items-center gap-2 text-[12px] text-[#6B7280] uppercase tracking-wider">
          <Filter size={14} /> Filtros
        </div>
        <FilterGroup
          value={filterStatus} setValue={setFilterStatus} testId="filter-status"
          options={[
            { v: 'todas', l: 'Todos los estados' },
            { v: 'pendiente', l: 'Pendiente' },
            { v: 'en_curso', l: 'En curso' },
            { v: 'completada', l: 'Completada' },
          ]}
        />
        <FilterGroup
          value={filterPriority} setValue={setFilterPriority} testId="filter-priority"
          options={[
            { v: 'todas', l: 'Todas las prioridades' },
            { v: 'alta', l: 'Alta' },
            { v: 'media', l: 'Media' },
            { v: 'baja', l: 'Baja' },
          ]}
        />
      </div>

      {/* Tasks table */}
      <div className="pv-card overflow-hidden">
        <table className="pv-table" data-testid="plan-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}></th>
              <th>Tarea</th>
              <th className="hidden md:table-cell" style={{ width: 130 }}>Categoría</th>
              <th style={{ width: 110 }}>Prioridad</th>
              <th className="hidden md:table-cell" style={{ width: 100 }}>Estimado</th>
              <th style={{ width: 180 }}>Estado</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => (
              <TaskRow key={t.id} task={t} onAdvance={() => advance(t)} />
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center text-[#6B7280] py-8">Sin tareas con esos filtros.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TaskRow({ task, onAdvance }) {
  const [open, setOpen] = useState(false);
  const p = PRIORITY[task.priority];
  const s = STATUS[task.status];
  const cat = CATEGORY_META[task.category] || { label: task.category };
  const StatusIcon = task.status === 'completada' ? CheckCircle2 : task.status === 'en_curso' ? PlayCircle : Circle;
  const hasDetail = task.what_is || task.steps?.length || task.done_when;

  return (
    <>
      <tr data-testid={`task-row-${task.id}`} className={open ? 'bg-[#FAFBFC]' : ''}>
        <td>
          <button onClick={onAdvance} className="text-[#6B7280] hover:text-[#2A9B68] transition-colors" title={STATUS_NEXT_LABEL[task.status]}>
            <StatusIcon size={18} strokeWidth={1.5} style={{ color: s.dot }} />
          </button>
        </td>
        <td>
          <button
            className="flex items-start gap-1.5 text-left w-full group"
            onClick={() => hasDetail && setOpen((o) => !o)}
            data-testid={`task-toggle-${task.id}`}
          >
            {hasDetail && (
              open
                ? <ChevronDown size={14} className="mt-1 text-[#6B7280] shrink-0" />
                : <ChevronRight size={14} className="mt-1 text-[#6B7280] group-hover:text-[#143A34] shrink-0" />
            )}
            <div>
              <div className="font-medium text-[#1C2329] group-hover:text-[#143A34]">{task.title}</div>
              <div className="text-[13px] text-[#6B7280] mt-0.5 max-w-2xl">{task.description}</div>
              {hasDetail && !open && (
                <div className="text-[11.5px] text-[#2A9B68] mt-1 font-medium">Ver plan de acción detallado ↓</div>
              )}
            </div>
          </button>
        </td>
        <td className="hidden md:table-cell">
          <span className="pv-chip"><span className="pv-chip-dot" />{cat.label}</span>
        </td>
        <td>
          <span
            className="inline-flex items-center gap-1.5 text-[12px] font-medium px-2 py-0.5 rounded"
            style={{ background: p.bg, color: p.color }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.color }} />
            {p.label}
          </span>
        </td>
        <td className="hidden md:table-cell">
          <span className="inline-flex items-center gap-1 text-[13px] text-[#374151]">
            <CalendarClock size={13} /> {task.estimated_minutes} min
          </span>
        </td>
        <td>
          <button
            onClick={onAdvance}
            className="text-[12px] font-medium px-2.5 py-1 rounded-md border transition-colors"
            style={{ borderColor: s.dot + '55', color: s.color, background: '#fff' }}
            data-testid={`task-advance-${task.id}`}
          >
            {STATUS_NEXT_LABEL[task.status]}
          </button>
        </td>
      </tr>
      {open && hasDetail && (
        <tr data-testid={`task-detail-${task.id}`} className="bg-[#FAFBFC]">
          <td></td>
          <td colSpan={5} className="py-5 pr-4">
            <div className="max-w-3xl space-y-4 text-[13.5px]">
              {task.what_is && (
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#6B7280] mb-1">¿Qué es?</div>
                  <div className="text-[#374151] leading-relaxed">{task.what_is}</div>
                </div>
              )}
              {task.why_matters && (
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#B91C1C] mb-1">¿Por qué importa?</div>
                  <div className="text-[#374151] leading-relaxed">{task.why_matters}</div>
                </div>
              )}
              {task.steps?.length > 0 && (
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#143A34] mb-2">Pasos concretos</div>
                  <ol className="space-y-1.5 list-decimal pl-5 text-[#1C2329]">
                    {task.steps.map((step, i) => (
                      <li key={i} className="leading-relaxed">{step}</li>
                    ))}
                  </ol>
                </div>
              )}
              {task.priviax_link && (
                <div className="pt-2 border-t border-[#E5E7EB]">
                  <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#6B7280] mb-1.5">Hágalo en Priviax</div>
                  <Link
                    to={task.priviax_link}
                    className="inline-flex items-center gap-2 text-[13px] text-[#143A34] font-medium hover:underline"
                    data-testid={`task-priviax-link-${task.id}`}
                  >
                    {task.priviax_hint || 'Ir al módulo'} <ArrowRight size={13} />
                  </Link>
                </div>
              )}
              {task.done_when && (
                <div className="pt-2 border-t border-[#E5E7EB] bg-[#F0FBF5] -mx-1 px-3 py-2.5 rounded">
                  <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#065F46] mb-1 flex items-center gap-1.5">
                    <CheckCircle2 size={11} /> Listo cuando
                  </div>
                  <div className="text-[#065F46] leading-relaxed">{task.done_when}</div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="pv-card p-4">
      <div className="pv-eyebrow">{label}</div>
      <div className="text-[24px] font-semibold tabular-nums mt-1" style={{ color: color || '#1C2329' }}>{value}</div>
    </div>
  );
}

function FilterGroup({ value, setValue, options, testId }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {options.map((o) => (
        <button
          key={o.v}
          onClick={() => setValue(o.v)}
          className={`text-[12px] px-2.5 py-1 rounded-md border transition-colors ${value === o.v ? 'bg-[#0B1220] text-white border-[#0B1220]' : 'bg-white text-[#374151] border-[#E5E7EB] hover:bg-[#FAFBFC]'}`}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}
